import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Box, Flex, useMediaQuery } from "@chakra-ui/react";
import { AnimatePresence } from "framer-motion";
import { MotionBox, MotionFlex } from "utils/motion";
import Button from "components/base/Button";
import Text from "components/base/Text";
import Trans from "components/base/Trans";
import { useTidioLauncherOffset } from "utils/hooks";
import {
  PREORDER_CLOSURE_DATE,
  calculateTimeLeft,
  getClosureLabels,
} from "utils/preorder";

// Pre-orders close at 23:59 GMT on Thursday 19 November 2026.
export const PREORDER_DEADLINE = PREORDER_CLOSURE_DATE;

export { calculateTimeLeft };

// The mobile dock shows its details on arrival, then tucks them away.
const AUTO_COLLAPSE_MS = 10000;

const SEGMENTS = [
  { key: "days", label: "LBL_PREORDER_COUNTDOWN_DAYS" },
  { key: "hours", label: "LBL_PREORDER_COUNTDOWN_HOURS" },
  { key: "minutes", label: "LBL_PREORDER_COUNTDOWN_MINUTES" },
];

const dockVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const glassPanel = {
  bg: "rgba(18, 18, 18, 0.92)",
  backdropFilter: "blur(12px)",
  border: "1px solid",
  borderColor: "whiteAlpha.200",
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
};

const pad = (value) => String(value).padStart(2, "0");

const Countdown = ({ timeLeft }) => (
  <Flex
    align="center"
    gap={{ base: 2, sm: 4 }}
    role="timer"
    aria-live="off"
    flexShrink={0}
  >
    {SEGMENTS.map(({ key, label }) => (
      <Flex key={key} direction="column" align="center" minW="48px">
        <Text
          as="span"
          type="headline_sm"
          color="white"
          lineHeight="1"
          sx={{ fontVariantNumeric: "tabular-nums" }}
          suppressHydrationWarning
        >
          {timeLeft ? pad(timeLeft[key]) : "--"}
        </Text>
        <Text as="span" type="title_xxs" color="whiteAlpha.600" mt={1}>
          <Trans id={label} />
        </Text>
      </Flex>
    ))}
  </Flex>
);

const PreorderCountdownBanner = () => {
  const { locale } = useRouter() || {};
  // Calculated on first render so the server emits the countdown too.
  // null = deadline passed.
  const [timeLeft, setTimeLeft] = useState(() =>
    calculateTimeLeft(PREORDER_CLOSURE_DATE),
  );
  const [isExpanded, setIsExpanded] = useState(true);
  // Only the first reveal collapses itself; manual re-opens stay open.
  const autoCollapsePending = useRef(true);
  const mobileDockRef = useRef(null);
  const [mobileDockHeight, setMobileDockHeight] = useState(0);
  const [isMobileViewport] = useMediaQuery("(max-width: 991px)");

  useEffect(() => {
    const update = () => setTimeLeft(calculateTimeLeft(PREORDER_CLOSURE_DATE));

    update();
    const interval = setInterval(update, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isExpanded || !autoCollapsePending.current) return;

    const timer = setTimeout(() => {
      autoCollapsePending.current = false;
      setIsExpanded(false);
    }, AUTO_COLLAPSE_MS);

    return () => clearTimeout(timer);
  }, [isExpanded]);

  // Sit the Tidio launcher on the mobile dock's top edge, with 24px of overlap,
  // whether its details are open or closed. The offset follows the dock height.
  useEffect(() => {
    const element = mobileDockRef.current;
    if (!element) return undefined;

    let timer;
    const measure = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const nextHeight = element.offsetHeight;
        setMobileDockHeight((current) =>
          Math.abs(current - nextHeight) > 1 ? nextHeight : current,
        );
      }, 120);
    };

    measure();
    window.addEventListener("resize", measure);

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(element);
    }

    return () => {
      clearTimeout(timer);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useTidioLauncherOffset(
    mobileDockHeight - 24,
    isMobileViewport && mobileDockHeight > 0 && Boolean(timeLeft),
  );

  const handleToggle = () => {
    autoCollapsePending.current = false;
    setIsExpanded((expanded) => !expanded);
  };

  if (timeLeft === null) return null;

  const { date: closureDate, time: closureTime } = getClosureLabels(locale);

  return (
    <>
      {/* Desktop: floating dock */}
      <MotionFlex
        data-testid="preorder-countdown-desktop"
        display={{ base: "none", lg: "flex" }}
        position="fixed"
        left={0}
        right={0}
        bottom="24px"
        mx="auto"
        w="calc(100% - 48px)"
        maxW="1200px"
        sx={{
          // Keep a left gutter for the Tidio launcher until the centred dock
          // clears it on its own (~1376px).
          "@media (max-width: 1375px)": {
            left: "96px",
            right: "24px",
            mx: 0,
            w: "auto",
          },
        }}
        zIndex={101}
        direction="column"
        gap={4}
        px={6}
        py={4}
        {...glassPanel}
        variants={dockVariants}
        initial="hidden"
        animate="visible"
      >
        <Flex align="baseline" justify="space-between" gap={6}>
          <Text type="title_sm" color="primary" flexShrink={0}>
            <Trans id="LBL_PREORDER_COUNTDOWN_STATUS" />
          </Text>
          <Text type="p_sm" color="whiteAlpha.800" textAlign="right">
            <Trans
              id="LBL_PREORDER_COUNTDOWN_MESSAGE"
              values={{ date: closureDate, time: closureTime }}
            />
          </Text>
        </Flex>

        <Flex align="center" justify="space-between" gap={6}>
          <Countdown timeLeft={timeLeft} />

          <Flex align="center" gap={3} flexShrink={0}>
            <Button
              href="/schedule"
              variant="outline"
              trackLabel="countdown_banner_schedule"
            >
              <Trans id="LBL_PREORDER_COUNTDOWN_CTA_SCHEDULE" />
            </Button>
            <Button
              href="/preorder"
              variant="solid_primary"
              trackLabel="countdown_banner_preorder"
            >
              <Trans id="LBL_PREORDER_COUNTDOWN_CTA_PREORDER" />
            </Button>
          </Flex>
        </Flex>
      </MotionFlex>

      {/* Mobile: adaptive expandable dock */}
      <MotionFlex
        ref={mobileDockRef}
        data-testid="preorder-countdown-mobile"
        display={{ base: "flex", lg: "none" }}
        position="fixed"
        left={0}
        right={0}
        bottom={0}
        px="12px"
        sx={{ pb: "calc(12px + env(safe-area-inset-bottom))" }}
        zIndex={101}
        variants={dockVariants}
        initial="hidden"
        animate="visible"
      >
        <MotionFlex direction="column" w="100%" px={4} py={3} {...glassPanel}>
          <Flex align="center" justify="space-between" gap={3}>
            <Text type="title_sm" color="primary">
              <Trans id="LBL_PREORDER_COUNTDOWN_STATUS" />
            </Text>

            <Button
              onClick={handleToggle}
              size="small"
              variant="outline"
              flexShrink={0}
              aria-expanded={isExpanded}
              aria-controls="preorder-countdown-details"
            >
              <Trans
                id={
                  isExpanded
                    ? "LBL_PREORDER_COUNTDOWN_HIDE_DETAILS"
                    : "LBL_PREORDER_COUNTDOWN_SHOW_DETAILS"
                }
              />
            </Button>
          </Flex>

          <AnimatePresence initial={false}>
            {isExpanded && (
              <MotionBox
                key="details"
                id="preorder-countdown-details"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                overflow="hidden"
              >
                <Box pt={4}>
                  <Text type="p_sm" color="whiteAlpha.800" mb={4}>
                    <Trans
                      id="LBL_PREORDER_COUNTDOWN_MESSAGE"
                      values={{ date: closureDate, time: closureTime }}
                    />
                  </Text>

                  <Box pt={3}>
                    <Countdown timeLeft={timeLeft} />
                  </Box>

                  <Flex direction="column" gap={2} mt={5}>
                    <Button
                      href="/preorder"
                      variant="solid_primary"
                      trackLabel="countdown_banner_preorder"
                      width="100%"
                    >
                      <Trans id="LBL_PREORDER_COUNTDOWN_CTA_PREORDER" />
                    </Button>
                    <Button
                      href="/schedule"
                      variant="outline"
                      trackLabel="countdown_banner_schedule"
                      width="100%"
                    >
                      <Trans id="LBL_PREORDER_COUNTDOWN_CTA_SCHEDULE" />
                    </Button>
                  </Flex>
                </Box>
              </MotionBox>
            )}
          </AnimatePresence>
        </MotionFlex>
      </MotionFlex>
    </>
  );
};

export default PreorderCountdownBanner;
